/**
 * MyFoodLists — a single stop for the user's Wishlist, Shopping List,
 * and Pantry. Items move naturally through the pipeline (wish → shop →
 * pantry). Pantry rows flag expiring soon / expired / low stock.
 *
 * Route: /food-pulse/my-lists
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Heart, ShoppingCart, Home, ArrowRight, X, Plus, Search, Camera, Receipt,
  AlertTriangle, Clock, Loader2, Bell, Trash2, Sparkles,
} from "lucide-react";
import { http, API } from "@/lib/api";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import SeoHead from "@/components/SeoHead";
import ProductScannerModal from "@/components/ProductScannerModal";
import PantryItemActionsSheet from "@/components/PantryItemActionsSheet";
import PantryLocationsManager from "@/components/PantryLocationsManager";
import { Settings, Lock, Sparkles as SparkIcon } from "lucide-react";

const GREEN = "#0F3D2E";
const AMBER = "#E4A140";
const CREAM = "#FBF7EF";

function CatalogPickInline({ onPick, placeholder = "Search food items…" }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (q.length < 2) { setItems([]); return; }
    let cancel = false;
    setLoading(true);
    axios.get(`${API.replace(/\/api$/, "")}/api/food-items`, { params: { q, limit: 8 } })
      .then((r) => { if (!cancel) setItems(r.data.items || r.data || []); })
      .catch(() => !cancel && setItems([]))
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [q]);
  return (
    <div className="relative">
      <div className="flex items-center gap-2 rounded-full border px-4 py-2" style={{ borderColor: "#E9D8B4", background: "#fff" }}>
        <Search className="w-4 h-4 opacity-50" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder}
               className="flex-1 outline-none text-sm bg-transparent" data-testid="lists-catalog-search" />
        {loading && <Loader2 className="w-4 h-4 animate-spin opacity-40" />}
      </div>
      {items.length > 0 && (
        <div className="absolute z-10 top-full mt-2 left-0 right-0 bg-white rounded-2xl border shadow-xl overflow-hidden" style={{ borderColor: "#E9D8B4" }}>
          {items.map((it) => (
            <button key={it.slug} onClick={() => { onPick(it); setQ(""); setItems([]); }}
                    className="w-full text-left px-4 py-2 hover:bg-black/5 flex items-center gap-3"
                    data-testid={`lists-pick-${it.slug}`}>
              {(it.hero_image || it.image_url) && <img src={it.hero_image || it.image_url} className="w-8 h-8 rounded-md object-cover" alt="" />}
              <div className="text-sm">{it.name || it.slug}</div>
              <ArrowRight className="w-4 h-4 ml-auto opacity-40" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MyFoodLists() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [urlParams] = useSearchParams();
  const [tab, setTab] = useState(urlParams.get("tab") || "wishlist");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [wishlist, setWishlist] = useState([]);
  const [shopping, setShopping] = useState([]);
  const [pantry, setPantry] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    try {
      const [w, s, p, a] = await Promise.all([
        http.get(`/lists/wishlist`),
        http.get(`/lists/shopping`),
        http.get(`/lists/pantry`),
        http.get(`/lists/wishlist/alerts`).catch(() => ({ data: { items: [] } })),
      ]);
      setWishlist(w.data.items || []);
      setShopping(s.data.items || []);
      setPantry(p.data.items || []);
      setAlerts(a.data.items || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [user]);
  useEffect(() => { reload(); }, [reload]);

  const addWish = async (item) => {
    try { const r = await http.post(`/lists/wishlist`, { food_item_slug: item.slug, food_item_name: item.name, food_item_image: item.hero_image || item.image_url }); setWishlist((p) => [r.data, ...p.filter(x => x.id !== r.data.id)]); }
    catch (e) { alert(e?.response?.data?.detail || "Add failed"); }
  };
  const addShop = async (item) => {
    try { const r = await http.post(`/lists/shopping`, { food_item_slug: item.slug, food_item_name: item.name, food_item_image: item.hero_image || item.image_url, quantity: 1 }); setShopping((p) => [r.data, ...p]); }
    catch (e) { alert(e?.response?.data?.detail || "Add failed"); }
  };

  const wishToShop = async (id) => {
    try {
      const r = await http.post(`/lists/wishlist/${id}/move-to-shopping`);
      setShopping((p) => [r.data, ...p]);
      alert("Added to shopping list.");
    } catch (e) { alert(e?.response?.data?.detail || "Move failed"); }
  };

  const purchaseShopping = async (row) => {
    const qtyStr = prompt(`How many ${row.unit || "units"} did you buy?`, String(row.quantity || 1));
    if (!qtyStr) return;
    const priceStr = prompt(`Purchase price per ${row.unit || "unit"}? (leave blank if unknown)`, "");
    const expiryStr = prompt("Expiry date? (YYYY-MM-DD, blank = none)", "");
    const location = prompt("Storage location? (e.g. fridge-top, pantry-shelf-2)", "") || undefined;
    try {
      const pantryDoc = await http.post(`/lists/shopping/${row.id}/move-to-pantry`, {
        food_item_slug: row.food_item_slug,
        food_item_name: row.food_item_name,
        food_item_image: row.food_item_image,
        quantity: Number(qtyStr),
        unit: row.unit || "each",
        purchase_price: priceStr ? Number(priceStr) : undefined,
        expiry_date: expiryStr || undefined,
        storage_location: location,
      });
      setPantry((p) => [pantryDoc.data, ...p]);
      setShopping((p) => p.map((x) => x.id === row.id ? { ...x, checked: true } : x));
      setTab("pantry");
    } catch (e) { alert(e?.response?.data?.detail || "Purchase failed"); }
  };

  const del = async (list, id) => {
    if (!confirm("Delete?")) return;
    try {
      await http.delete(`/lists/${list}/${id}`);
      if (list === "wishlist") setWishlist((p) => p.filter(x => x.id !== id));
      if (list === "shopping") setShopping((p) => p.filter(x => x.id !== id));
      if (list === "pantry")   setPantry((p) => p.filter(x => x.id !== id));
    } catch (e) { alert(e?.response?.data?.detail || "Delete failed"); }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: CREAM, color: GREEN }}>
        <div className="max-w-md text-center">
          <Heart className="w-14 h-14 mx-auto mb-4" style={{ color: AMBER }} />
          <h1 className="font-display text-3xl mb-3">Your food lists</h1>
          <p className="opacity-80 mb-6">Sign in to build your wishlist, plan shopping, and track your pantry — including alerts before food expires.</p>
          <Link to="/login" className="rounded-full px-6 py-3 font-semibold text-white" style={{ background: GREEN }} data-testid="lists-login-cta">Sign in</Link>
        </div>
      </div>
    );
  }

  const expiringSoon = pantry.filter((p) => p.is_expiring_soon || p.is_expired || p.is_low_stock);

  return (
    <div className="min-h-screen" style={{ background: CREAM, color: GREEN }} data-testid="my-food-lists">
      <SeoHead section="food-pulse-my-lists" title="My food lists — Food Pulse" description="Wishlist, shopping list, and pantry with expiry alerts." />

      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="mb-6">
          <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: AMBER }}>— My lists</div>
          <h1 className="font-display text-4xl">Wish. Shop. Cook. Waste less.</h1>
        </div>

        {expiringSoon.length > 0 && (
          <div className="rounded-2xl p-4 mb-6 flex items-start gap-3" style={{ background: "#FEE2E2", border: "1px solid #F87171" }} data-testid="expiring-alert">
            <AlertTriangle className="w-5 h-5 text-red-700 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-red-800">
              <div className="font-semibold">{expiringSoon.length} pantry item{expiringSoon.length === 1 ? "" : "s"} need{expiringSoon.length === 1 ? "s" : ""} attention</div>
              <div className="opacity-90">{expiringSoon.slice(0, 3).map(x => x.food_item_name).join(", ")}{expiringSoon.length > 3 ? "…" : ""}. Tap Pantry to review.</div>
            </div>
          </div>
        )}

        <div className="mb-6 flex flex-wrap gap-2">
          <button
            onClick={() => nav("/food-pulse/lists/wishlist/new")}
            className="rounded-full px-4 py-2 text-sm font-semibold text-white flex items-center gap-2"
            style={{ background: GREEN }}
            data-testid="mylists-add-wish"
          >
            <Plus className="w-4 h-4" /> Add Wish Product
          </button>
          <button
            onClick={() => setScannerOpen(true)}
            className="rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2 border-2"
            style={{ borderColor: GREEN, color: GREEN, background: "#fff" }}
            data-testid="mylists-scan"
          >
            <Camera className="w-4 h-4" /> Scan Product
          </button>
          <Link
            to="/food-pulse/receipts/new"
            className="rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2 border-2"
            style={{ borderColor: AMBER, color: GREEN, background: "#FFF7E4" }}
            data-testid="mylists-scan-receipt"
          >
            <Receipt className="w-4 h-4" /> Snap Receipt
          </Link>
          <Link
            to="/food-pulse/household"
            className="rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2 border-2"
            style={{ borderColor: GREEN, color: "white", background: GREEN }}
            data-testid="mylists-household"
          >
            <Home className="w-4 h-4" /> Household Dashboard
          </Link>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { k: "wishlist", l: `Wishlist (${wishlist.length})`, icon: Heart },
            { k: "shopping", l: `Shopping (${shopping.filter(s => !s.checked).length})`, icon: ShoppingCart },
            { k: "pantry",   l: `Pantry (${pantry.length})`, icon: Home },
            { k: "alerts",   l: `Alerts (${alerts.length})`, icon: Bell, highlight: alerts.length > 0 },
          ].map(({ k, l, icon: Ico, highlight }) => (
            <button key={k} onClick={() => setTab(k)}
                    className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${tab === k ? "text-white" : "opacity-70 hover:opacity-100"} ${highlight && tab !== k ? "ring-2 ring-amber-300" : ""}`}
                    style={{ background: tab === k ? GREEN : "transparent" }}
                    data-testid={`lists-tab-${k}`}>
              <Ico className="w-4 h-4" /> {l}
            </button>
          ))}
        </div>

        {loading ? <div className="text-center py-12 opacity-60">Loading…</div> : (
          <>
            {tab === "wishlist" && (
              <div>
                {wishlist.length === 0 ? (
                  <EmptyState icon={Heart} title="Start your wishlist" body="Tap 'Add Wish Product' above or 'Scan Product' to snap a barcode." />
                ) : (
                  <div className="grid gap-3">
                    {wishlist.map((row) => (
                      <ListRow key={row.id} row={row}>
                        <button onClick={() => wishToShop(row.id)} className="rounded-full px-3 py-1.5 text-xs font-semibold text-white" style={{ background: AMBER, color: GREEN }}
                                data-testid={`wish-to-shop-${row.id}`}>
                          → Shopping
                        </button>
                        <button onClick={() => del("wishlist", row.id)} className="rounded-full p-1.5 border border-red-200 text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
                      </ListRow>
                    ))}
                  </div>
                )}
              </div>
            )}

            {tab === "shopping" && (
              <div>
                <div className="mb-4"><CatalogPickInline onPick={addShop} placeholder="Add to shopping list…" /></div>
                {shopping.length === 0 ? (
                  <EmptyState icon={ShoppingCart} title="Nothing on the shopping list" body="Add things you need to buy on your next trip." />
                ) : (
                  <div className="grid gap-3">
                    {shopping.map((row) => (
                      <ListRow key={row.id} row={row} extra={row.quantity ? `${row.quantity} ${row.unit || ""}` : null} muted={row.checked}>
                        {!row.checked && (
                          <button onClick={() => purchaseShopping(row)} className="rounded-full px-3 py-1.5 text-xs font-semibold text-white" style={{ background: GREEN }}
                                  data-testid={`shop-purchased-${row.id}`}>
                            → Purchased
                          </button>
                        )}
                        <button onClick={() => del("shopping", row.id)} className="rounded-full p-1.5 border border-red-200 text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
                      </ListRow>
                    ))}
                  </div>
                )}
              </div>
            )}

            {tab === "pantry" && (
              <PantryTab
                pantry={pantry}
                setPantry={setPantry}
                onDelete={(id) => del("pantry", id)}
              />
            )}

            {tab === "alerts" && (
              <div data-testid="alerts-tab">
                {alerts.length === 0 ? (
                  <EmptyState icon={Bell} title="No alerts yet" body="We'll ping you the moment a new deal drops on anything in your wishlist — both price matches (highlighted) and any fresh deals for the item." />
                ) : (
                  <div className="grid gap-3">
                    {alerts.map((a) => {
                      const d = a.deal || {};
                      const isPrice = a.match_type === "price_match";
                      return (
                        <div key={a.id} className={`rounded-2xl border p-3 flex items-center gap-3`}
                             style={{ borderColor: isPrice ? AMBER : "#E9D8B4", background: isPrice ? "#FFF7E4" : "#fff" }}
                             data-testid={`alert-row-${a.id}`}>
                          <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0"
                               style={{ background: `linear-gradient(135deg,#F6C87A,${CREAM})` }}>
                            {d.image_urls?.[0] && <img src={d.image_urls[0]} alt="" className="w-full h-full object-cover" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              {isPrice
                                ? <Sparkles className="w-4 h-4 flex-shrink-0" style={{ color: AMBER }} />
                                : <Bell className="w-4 h-4 flex-shrink-0 opacity-50" />}
                              <span className="text-[10px] font-mono uppercase tracking-widest"
                                    style={{ color: isPrice ? "#8B5E10" : GREEN }}>
                                {isPrice ? "Price match" : "New deal spotted"}
                              </span>
                            </div>
                            <div className="font-display text-base truncate">{d.title || a.deal_slug}</div>
                            <div className="text-[11px] opacity-70">
                              {d.deal_price != null && <>${d.deal_price}{d.unit ? `/${d.unit}` : ""} · </>}
                              {d.vendor_name || "vendor"} · {new Date(a.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                              {d.is_expired && <span className="ml-1 text-red-700">· expired</span>}
                            </div>
                          </div>
                          <Link to={`/food-pulse/deals/${a.deal_slug}`} className="rounded-full px-3 py-1.5 text-xs font-semibold text-white flex-shrink-0"
                                style={{ background: GREEN }}
                                data-testid={`alert-view-${a.id}`}>
                            View →
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <ProductScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onMatch={(product) => {
          setScannerOpen(false);
          nav("/food-pulse/lists/wishlist/new", { state: { product } });
        }}
      />
    </div>
  );
}

function ListRow({ row, extra, muted, children }) {
  return (
    <div className={`rounded-2xl border p-3 flex items-center gap-3 ${muted ? "opacity-50" : ""}`}
         style={{ borderColor: "#E9D8B4", background: "#fff" }}
         data-testid={`list-row-${row.id}`}>
      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0" style={{ background: `linear-gradient(135deg,#F6C87A,${CREAM})` }}>
        {row.food_item_image && <img src={row.food_item_image} alt="" className="w-full h-full object-cover" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-base truncate">{row.food_item_name}</div>
        {extra && <div className="text-[11px] opacity-60">{extra}</div>}
        {row.wish_price && <div className="text-[11px] opacity-70">Wish ≤ ${row.wish_price}</div>}
        {row.storage_location && <div className="text-[11px] opacity-60">@ {row.storage_location}</div>}
        {row.expiry_date && <div className="text-[11px] opacity-60">expires {row.expiry_date.slice(0, 10)}</div>}
      </div>
      <div className="flex items-center gap-2 flex-wrap justify-end">{children}</div>
    </div>
  );
}

function EmptyState({ icon: Ico, title, body }) {
  return (
    <div className="rounded-3xl border border-dashed p-14 text-center" style={{ borderColor: AMBER, background: "#FFF7E4" }}>
      <Ico className="w-14 h-14 mx-auto opacity-40 mb-3" />
      <div className="font-display text-2xl mb-2">{title}</div>
      <div className="opacity-70 text-sm max-w-md mx-auto">{body}</div>
    </div>
  );
}


/* ================================================================
   Smart Pantry Tab (Phase 3)
   ================================================================ */
const PANTRY_STATUS_FILTERS = [
  { key: "in-stock", label: "In stock",  color: "#059669" },
  { key: "opened",   label: "Opened",    color: "#3B82F6" },
  { key: "low",      label: "Low",       color: "#F59E0B" },
  { key: "expired",  label: "Expired",   color: "#DC2626" },
];

function PantryTab({ pantry, setPantry, onDelete }) {
  const [locations, setLocations] = useState([]);
  const [locFilter,  setLocFilter]  = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [locModalOpen, setLocModalOpen] = useState(false);
  const [actionItem, setActionItem] = useState(null);

  const loadLocations = useCallback(async () => {
    try {
      const r = await http.get("/pantry/locations");
      setLocations(r.data?.items || []);
    } catch { setLocations([]); }
  }, []);

  useEffect(() => { loadLocations(); }, [loadLocations]);

  const filtered = pantry.filter((row) => {
    if (locFilter !== "all") {
      if (locFilter === "none" && row.storage_location_id) return false;
      if (locFilter !== "none" && row.storage_location_id !== locFilter) return false;
    }
    if (statusFilter !== "all") {
      if (statusFilter === "in-stock") {
        return !row.status || row.status === "in-stock";
      }
      return row.status === statusFilter;
    }
    return !["consumed","wasted","donated","missing","expired"].includes(row.status || "");
  });

  const onItemChange = (updated) => {
    if (!updated) return;
    setPantry((prev) => prev.map((r) => r.id === updated.id ? { ...r, ...updated } : r));
  };

  return (
    <div data-testid="pantry-tab">
      {/* Filters ---------------------------------------- */}
      <div className="flex items-center flex-wrap gap-2 mb-3">
        <select value={locFilter} onChange={(e) => setLocFilter(e.target.value)}
                className="rounded-full border-2 px-3 py-1.5 text-xs font-semibold bg-white"
                style={{ borderColor: "#E9D8B4", color: GREEN }}
                data-testid="pantry-location-filter">
          <option value="all">All locations · {pantry.length}</option>
          <option value="none">— Unassigned —</option>
          {locations.map((l) => (
            <option key={l.id} value={l.id}>
              {l.icon || "📦"} {l.is_private ? (l.alias || l.name) : l.name} · {l.item_count}
            </option>
          ))}
        </select>

        {PANTRY_STATUS_FILTERS.map((s) => (
          <button key={s.key} onClick={() => setStatusFilter(statusFilter === s.key ? "all" : s.key)}
                  className="rounded-full px-3 py-1 text-[11px] font-semibold border-2 transition"
                  style={{
                    background: statusFilter === s.key ? s.color : "transparent",
                    color: statusFilter === s.key ? "white" : GREEN,
                    borderColor: statusFilter === s.key ? s.color : "#E9D8B4",
                  }}
                  data-testid={`pantry-status-${s.key}`}>
            {s.label}
          </button>
        ))}

        <button onClick={() => setLocModalOpen(true)}
                className="ml-auto rounded-full px-3 py-1.5 text-xs font-semibold flex items-center gap-1 border-2"
                style={{ borderColor: GREEN, color: GREEN, background: "white" }}
                data-testid="pantry-manage-locations">
          <Settings className="w-3.5 h-3.5" /> Manage locations
        </button>
      </div>

      {filtered.length === 0 ? (
        pantry.length === 0
          ? <EmptyState icon={Home} title="Your pantry is empty"
                        body="Snap a grocery receipt — items auto-add with an estimated expiry." />
          : <div className="rounded-2xl border border-dashed p-8 text-center opacity-70 text-sm"
                 style={{ borderColor: "#E9D8B4" }}>
              No pantry items match this filter.
            </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map((row) => (
            <PantryRow key={row.id} row={row} locations={locations}
                       onOpenActions={() => setActionItem(row)}
                       onDelete={() => onDelete(row.id)} />
          ))}
        </div>
      )}

      <PantryItemActionsSheet
        item={actionItem}
        locations={locations}
        open={!!actionItem}
        onClose={() => setActionItem(null)}
        onChange={(updated) => { onItemChange(updated); loadLocations(); }}
      />
      <PantryLocationsManager
        open={locModalOpen}
        onClose={() => setLocModalOpen(false)}
        onChange={loadLocations}
      />
    </div>
  );
}

function PantryRow({ row, locations, onOpenActions, onDelete }) {
  const loc = locations.find((l) => l.id === row.storage_location_id);
  const locLabel = loc ? (loc.is_private ? (loc.alias || loc.name) : loc.name) : (row.storage_location_name || null);

  return (
    <div className="rounded-2xl border bg-white p-3 flex items-center gap-3"
         style={{ borderColor: "#E9D8B4" }}
         data-testid={`pantry-row-${row.id}`}>
      <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0"
           style={{ background: `linear-gradient(135deg,#F6C87A,${CREAM})` }}>
        {(row.photos?.[0]?.url || row.food_item_image) && (
          <img src={(row.photos?.[0]?.url || row.food_item_image)} alt=""
               className="w-full h-full object-cover" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-base truncate">{row.food_item_name}</div>
        <div className="text-[11px] opacity-60">
          {row.quantity} {row.unit || "each"}
          {row.expiry_date && <> · exp {row.expiry_date.slice(0, 10)}</>}
          {locLabel && (
            <> · <span className="inline-flex items-center gap-0.5">{loc?.is_private && <Lock className="w-2.5 h-2.5" />}{locLabel}</span></>
          )}
        </div>
        {row.status && row.status !== "in-stock" && (
          <span className="mt-1 inline-block rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                style={{ background: statusColor(row.status), color: "white" }}>
            {row.status}
          </span>
        )}
        {row.is_expired && !row.status && (
          <span className="mt-1 inline-block rounded-full px-1.5 py-0.5 text-[10px] font-bold text-white" style={{ background: "#DC2626" }}>expired</span>
        )}
        {!row.is_expired && row.is_expiring_soon && (
          <span className="mt-1 inline-block rounded-full px-1.5 py-0.5 text-[10px] font-bold text-white ml-1" style={{ background: "#F59E0B" }}>
            {row.days_until_expiry === 0 ? "expires today" : `${row.days_until_expiry}d left`}
          </span>
        )}
      </div>
      <button onClick={onOpenActions}
              className="rounded-full px-3 py-1.5 text-xs font-semibold text-white flex items-center gap-1"
              style={{ background: GREEN }}
              data-testid={`pantry-row-actions-${row.id}`}>
        <SparkIcon className="w-3 h-3" /> Actions
      </button>
      <button onClick={onDelete}
              className="rounded-full p-1.5 border border-red-200 text-red-700"
              data-testid={`pantry-row-delete-${row.id}`}>
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function statusColor(s) {
  return ({
    "opened": "#3B82F6", "low": "#F59E0B", "needs-restock": "#F59E0B",
    "expired": "#DC2626", "consumed": "#059669", "wasted": "#7B2A2E",
    "donated": "#7B2A2E", "missing": "#6B7280", "returned": "#6B7280",
  })[s] || GREEN;
}
